import { useEffect, useRef, useState } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import useEmblaCarousel from 'embla-carousel-react'
import Autoplay from 'embla-carousel-autoplay'
import { useTranslation } from '@/i18n'

import logoAndreea from '@/assets/clients/Logo-Andreea-Petcu-color-main.png'
import logoDoina from '@/assets/clients/Doina_Ielciu_-_color-main.png'
import logoEproprietar from '@/assets/clients/logo_eproprietar.png'
import logoExp from '@/assets/clients/eXp_Logo_Black.png'
import logoNextLevel from '@/assets/clients/logo-next_level_house.png'
import logoSorin from '@/assets/clients/sorin-sucala-black.png'
// The four below are traced from their PNG logos (none has a vector file);
// the sources are in documentation/media/portfolio_media/client-cards/logos/.
// Padel's crest is white there and recoloured navy here for the light page;
// Cristina Bara's lost its white badge, so her lettering is cut out of the bar.
import logoNaturalBeauty from '@/assets/clients/natural-beauty.svg'
import logoPovestela from '@/assets/clients/povestela-ofir.svg'
import logoCristinaBara from '@/assets/clients/cristina-bara.svg'
import logoPadel from '@/assets/clients/padel-crest.svg'

const clients = [
  { name: 'Andreea Stefania Petcu', logo: logoAndreea, invert: false },
  { name: 'Doina Ielciu', logo: logoDoina, invert: false },
  { name: 'eProprietar', logo: logoEproprietar, invert: false },
  { name: 'eXp', logo: logoExp, invert: true },
  { name: 'Natural Beauty', logo: logoNaturalBeauty, invert: true },
  { name: 'Next Level House', logo: logoNextLevel, invert: true },
  { name: 'Povestela Ofir', logo: logoPovestela, invert: true },
  { name: 'Sorin Sucala', logo: logoSorin, invert: true },
  { name: 'Cristina Bara', logo: logoCristinaBara, invert: true },
  { name: 'Padel by Club Transilvania', logo: logoPadel, invert: true },
]

export function SocialProof() {
  const { t } = useTranslation()
  // Embla's autoplay runs from JS, so it is invisible to both of this project's
  // reduced-motion layers: the CSS block in index.css only caps CSS durations,
  // and MotionConfig only governs framer-motion. It has to be asked directly.
  //
  // No visible pause button, by design choice: hovering the strip stops it
  // instead, and reduced-motion visitors never see it move at all.
  const reduceMotion = useReducedMotion()
  const autoplay = useRef(
    Autoplay({ delay: 3000, stopOnInteraction: false, stopOnMouseEnter: true, playOnInit: !reduceMotion })
  )
  // The logo in the centre is the one in colour, so the strip lights each in
  // turn as it passes, on phones too (a hover highlight never fires there).
  // Hence align 'center': with 'start' there is no single middle slide.
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: 'center', slidesToScroll: 1 },
    [autoplay.current]
  )
  const [selected, setSelected] = useState(0)
  useEffect(() => {
    if (!emblaApi) return
    const onSelect = () => setSelected(emblaApi.selectedScrollSnap())
    onSelect()
    emblaApi.on('select', onSelect).on('reInit', onSelect)
    return () => {
      emblaApi.off('select', onSelect).off('reInit', onSelect)
    }
  }, [emblaApi])

  // No band of its own (no tinted ground, no borders): the logos sit on the
  // page canvas so they read as part of the flow, not a separate block.
  return (
    <section className="py-12 sm:py-16">
      <div className="max-w-6xl mx-auto px-6 sm:px-8 text-center">
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-sm font-semibold text-muted-foreground uppercase tracking-widest mb-6"
        >
          {t.socialProof.trustedBy}
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex min-h-[56px] sm:min-h-[64px]" style={{ willChange: 'transform' }}>
              {clients.map((client, i) => (
                <div
                  key={client.name}
                  className="flex-[0_0_60%] sm:flex-[0_0_40%] md:flex-[0_0_25%] min-w-0 flex items-center justify-center px-3 sm:px-5 h-14 sm:h-16"
                >
                  <img
                    src={client.logo}
                    alt={client.name}
                    width={256}
                    height={64}
                    loading="lazy"
                    decoding="async"
                    className={`h-14 sm:h-16 w-auto max-w-full object-contain transition-all duration-500 ${
                      i === selected ? 'opacity-100' : 'grayscale opacity-60'
                    } ${client.invert ? 'dark:brightness-0 dark:invert' : ''}`}
                  />
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}
